/** Default rows returned when the LLM omits `limit` on query_resource. */
export const DEFAULT_QUERY_RESOURCE_LIMIT = 50;

/** Hard cap on rows per query_resource call (client-enforced). */
export const MAX_QUERY_RESOURCE_LIMIT = 100;

/** Default compact output for broad deterministic resource scans. */
export const DEFAULT_SCAN_OUTPUT = "count";

/** Default IDs returned when the LLM omits `maxIds` on scan_resource. */
export const DEFAULT_SCAN_MAX_IDS = 500;

/** Hard cap on IDs per scan_resource call. */
export const MAX_SCAN_IDS = 1_000;

/** Default rows returned when the LLM omits `maxRows` on field-returning scan_resource calls. */
export const DEFAULT_SCAN_MAX_ROWS = 100;

/** Hard cap on rows per field-returning scan_resource call. */
export const MAX_SCAN_ROWS = 100;

/** Default rows per analyze_resource_batches one-shot call. */
export const DEFAULT_BATCH_SIZE = 15;

/** Hard cap on rows per analyze_resource_batches one-shot call. */
export const MAX_BATCH_SIZE = 25;

/** Default number of analyze_resource_batches one-shot calls. */
export const DEFAULT_BATCH_ANALYSIS_MAX_BATCHES = 10;

/** Hard cap on analyze_resource_batches one-shot calls. */
export const MAX_BATCH_ANALYSIS_MAX_BATCHES = 25;

/** Default matched IDs returned from analyze_resource_batches. */
export const DEFAULT_BATCH_ANALYSIS_MAX_MATCHED_IDS = 500;

/** Hard cap on matched IDs returned from analyze_resource_batches. */
export const MAX_BATCH_ANALYSIS_MAX_MATCHED_IDS = 1_000;

/** Default reasons returned from analyze_resource_batches. */
export const DEFAULT_BATCH_ANALYSIS_MAX_REASONS = 50;

/** Hard cap on reasons returned from analyze_resource_batches. */
export const MAX_BATCH_ANALYSIS_MAX_REASONS = 100;

/** Safety cap on serialized tool result size sent back to the chat API. */
export const MAX_TOOL_RESULT_BYTES = 400_000;

export function resolveQueryResourceLimit(requested?: number | null): number {
  if (requested == null || Number.isNaN(requested)) {
    return DEFAULT_QUERY_RESOURCE_LIMIT;
  }
  return Math.min(Math.max(1, Math.floor(requested)), MAX_QUERY_RESOURCE_LIMIT);
}

export type ResourceQueryLimitResult<T> = {
  items: T[];
  totalCount: number;
  truncated: boolean;
  limit: number;
};

export function applyResourceQueryLimit<T>(
  items: T[],
  requestedLimit?: number | null,
): ResourceQueryLimitResult<T> {
  const limit = resolveQueryResourceLimit(requestedLimit);
  const totalCount = items.length;
  const truncated = totalCount > limit;
  return {
    items: truncated ? items.slice(0, limit) : items,
    totalCount,
    truncated,
    limit,
  };
}

export type ResourceFilterOperator = "eq" | "partial" | "gte" | "gt" | "lte" | "lt" | "in" | "exists";

export type ResourceFilterMode = "query" | "scan";

export type ResourceFilterResult<T> = {
  items: T[];
  warnings: string[];
};

type ResourceFilterOptions = {
  mode?: ResourceFilterMode;
  resourceName: string;
  fields: Record<string, unknown>;
};

export function resolveScanMaxIds(requested?: number | null): number {
  if (requested == null || Number.isNaN(requested)) {
    return DEFAULT_SCAN_MAX_IDS;
  }
  return Math.min(Math.max(1, Math.floor(requested)), MAX_SCAN_IDS);
}

export function resolveScanMaxRows(requested?: number | null): number {
  if (requested == null || Number.isNaN(requested)) {
    return DEFAULT_SCAN_MAX_ROWS;
  }
  return Math.min(Math.max(1, Math.floor(requested)), MAX_SCAN_ROWS);
}

export function resolveBatchSize(requested?: number | null): number {
  if (requested == null || Number.isNaN(requested)) {
    return DEFAULT_BATCH_SIZE;
  }
  return Math.min(Math.max(1, Math.floor(requested)), MAX_BATCH_SIZE);
}

export function resolveBatchAnalysisMaxBatches(requested?: number | null): number {
  if (requested == null || Number.isNaN(requested)) {
    return DEFAULT_BATCH_ANALYSIS_MAX_BATCHES;
  }
  return Math.min(Math.max(1, Math.floor(requested)), MAX_BATCH_ANALYSIS_MAX_BATCHES);
}

export function resolveBatchAnalysisMaxMatchedIds(requested?: number | null): number {
  if (requested == null || Number.isNaN(requested)) {
    return DEFAULT_BATCH_ANALYSIS_MAX_MATCHED_IDS;
  }
  return Math.min(Math.max(1, Math.floor(requested)), MAX_BATCH_ANALYSIS_MAX_MATCHED_IDS);
}

export function resolveBatchAnalysisMaxReasons(requested?: number | null): number {
  if (requested == null || Number.isNaN(requested)) {
    return DEFAULT_BATCH_ANALYSIS_MAX_REASONS;
  }
  return Math.min(Math.max(1, Math.floor(requested)), MAX_BATCH_ANALYSIS_MAX_REASONS);
}

export type PageItemsResult<T> = {
  batchIndex: number;
  totalBatches: number;
  items: T[];
};

/** Slice items into fixed-size pages, stopping after maxPages. */
export function* pageItems<T>(
  items: T[],
  { pageSize, maxPages }: { pageSize: number; maxPages: number },
): Generator<PageItemsResult<T>> {
  const cappedItems = items.slice(0, pageSize * maxPages);
  const totalBatches = cappedItems.length === 0 ? 0 : Math.ceil(cappedItems.length / pageSize);

  for (let index = 0; index < cappedItems.length; index += pageSize) {
    yield {
      batchIndex: Math.floor(index / pageSize),
      totalBatches,
      items: cappedItems.slice(index, index + pageSize),
    };
  }
}

export function applyResourceFilters<T extends Record<string, any>>(
  items: T[],
  filter: Record<string, any> | undefined | null,
  { mode = "query", resourceName, fields }: ResourceFilterOptions,
): ResourceFilterResult<T> {
  if (!filter) {
    return { items, warnings: [] };
  }

  const warnings = new Set<string>();
  let filteredItems = items;

  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (!(key in fields)) {
      warnings.add(`Filter field "${key}" does not exist in resource "${resourceName}".`);
      continue;
    }

    const operators = getFilterOperators(value);
    filteredItems = filteredItems.filter((item) => {
      const itemValue = item[key];

      if (operators.length === 0) {
        return valuesEqual(itemValue, value);
      }

      return operators.every(([operator, filterValue]) => {
        if (mode === "query" && operator !== "eq" && operator !== "partial") {
          return itemValue === value;
        }

        if (!isResourceFilterOperator(operator)) {
          warnings.add(`Unsupported filter operator "${operator}" for field "${key}".`);
          return mode === "query" ? itemValue === value : false;
        }

        return matchesFilterOperator(itemValue, operator, filterValue, key, warnings);
      });
    });
  }

  return { items: filteredItems, warnings: Array.from(warnings) };
}

function getFilterOperators(value: unknown): Array<[string, any]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return [];
  }
  return Object.entries(value);
}

function isResourceFilterOperator(operator: string): operator is ResourceFilterOperator {
  return ["eq", "partial", "gte", "gt", "lte", "lt", "in", "exists"].includes(operator);
}

function matchesFilterOperator(
  itemValue: unknown,
  operator: ResourceFilterOperator,
  filterValue: unknown,
  fieldName: string,
  warnings: Set<string>,
): boolean {
  switch (operator) {
    case "eq":
      return valuesEqual(itemValue, filterValue);
    case "partial":
      return String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
    case "in":
      if (!Array.isArray(filterValue)) {
        warnings.add(`Filter operator "in" for field "${fieldName}" expects an array.`);
        return false;
      }
      return filterValue.some((value) => valuesEqual(itemValue, value));
    case "exists": {
      const exists = itemValue !== undefined && itemValue !== null && itemValue !== "";
      return filterValue ? exists : !exists;
    }
    case "gte":
    case "gt":
    case "lte":
    case "lt":
      return compareOrderedValues(itemValue, filterValue, operator, fieldName, warnings);
  }
}

function valuesEqual(itemValue: unknown, filterValue: unknown): boolean {
  if (typeof itemValue === "string" && typeof filterValue === "string") {
    return itemValue.toLowerCase().trim() === filterValue.toLowerCase().trim();
  }
  return itemValue === filterValue;
}

function compareOrderedValues(
  itemValue: unknown,
  filterValue: unknown,
  operator: "gte" | "gt" | "lte" | "lt",
  fieldName: string,
  warnings: Set<string>,
): boolean {
  const itemComparable = toComparable(itemValue);
  const filterComparable = toComparable(filterValue);

  if (itemComparable == null || filterComparable == null) {
    warnings.add(
      `Filter operator "${operator}" for field "${fieldName}" requires numeric or date-like values.`,
    );
    return false;
  }

  switch (operator) {
    case "gte":
      return itemComparable >= filterComparable;
    case "gt":
      return itemComparable > filterComparable;
    case "lte":
      return itemComparable <= filterComparable;
    case "lt":
      return itemComparable < filterComparable;
  }
}

function toComparable(value: unknown): number | null {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  if (looksDateLike(value)) {
    const time = Date.parse(value);
    return Number.isNaN(time) ? null : time;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) && value.trim() !== "" ? numeric : null;
}

function looksDateLike(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}/.test(value) || /^\d{4}-\d{2}-\d{2}T/.test(value);
}

type ResourceToolResult = {
  type?: string;
  resource?: string;
  question?: string;
  result?: unknown;
  totalCount?: number;
  truncated?: boolean;
  truncatedBySize?: boolean;
  truncatedReasons?: boolean;
  truncatedIds?: boolean;
  limit?: number;
  matchedIds?: string[];
  matchedCount?: number;
  scannedCount?: number;
  filteredCount?: number;
  analyzedCount?: number;
  batchCount?: number;
  reasons?: Array<{ id: string; reason: string }>;
  summaries?: string[];
  warnings?: string[];
};

/** Shrink oversized tool payloads before posting them to the chat edge function. */
export function compactToolResultForTransport(result: unknown): unknown {
  if (result == null || typeof result !== "object") {
    return result;
  }

  if (JSON.stringify(result).length <= MAX_TOOL_RESULT_BYTES) {
    return result;
  }

  const obj = result as ResourceToolResult;
  if (obj.type === "resource_batch_analysis") {
    return compactResourceBatchAnalysisForTransport(obj);
  }

  if (obj.type === "resource" && Array.isArray(obj.result)) {
    const allItems = obj.result;
    let keep = allItems.length;
    while (keep > 1) {
      keep = Math.max(1, Math.floor(keep * 0.7));
      const compacted: ResourceToolResult = {
        ...obj,
        result: allItems.slice(0, keep),
        totalCount: obj.totalCount ?? allItems.length,
        truncated: true,
        truncatedBySize: true,
        limit: keep,
      };
      if (JSON.stringify(compacted).length <= MAX_TOOL_RESULT_BYTES) {
        return compacted;
      }
    }
  }

  return {
    error:
      "Tool result too large to send to the assistant. Retry with fewer fields, a smaller limit, or a narrower filter.",
    truncatedBySize: true,
  };
}

function shrinkArrayUntilFits<T>(
  values: T[],
  apply: (next: T[] | undefined) => ResourceToolResult,
  _current: ResourceToolResult,
  fits: (value: ResourceToolResult) => boolean,
): ResourceToolResult {
  let nextValues = [...values];
  let next = apply(nextValues);

  while (nextValues.length > 0 && !fits(next)) {
    if (nextValues.length === 1) {
      return apply(undefined);
    }
    nextValues = nextValues.slice(0, Math.max(1, Math.floor(nextValues.length / 2)));
    next = apply(nextValues);
  }

  return next;
}

function compactResourceBatchAnalysisForTransport(result: ResourceToolResult): unknown {
  if (JSON.stringify(result).length <= MAX_TOOL_RESULT_BYTES) {
    return result;
  }

  let current: ResourceToolResult = { ...result };
  const fits = (value: ResourceToolResult = current) => JSON.stringify(value).length <= MAX_TOOL_RESULT_BYTES;

  if (!fits() && Array.isArray(current.summaries) && current.summaries.length > 0) {
    current = shrinkArrayUntilFits(
      current.summaries,
      (summaries) => ({
        ...current,
        summaries,
        truncatedBySize: true,
      }),
      current,
      fits,
    );
  }

  if (!fits() && Array.isArray(current.reasons) && current.reasons.length > 0) {
    current = shrinkArrayUntilFits(
      current.reasons,
      (reasons) => ({
        ...current,
        reasons,
        truncatedReasons: true,
        truncatedBySize: true,
      }),
      current,
      fits,
    );
  }

  if (!fits() && Array.isArray(current.matchedIds) && current.matchedIds.length > 0) {
    current = shrinkArrayUntilFits(
      current.matchedIds,
      (matchedIds) => ({
        ...current,
        matchedIds,
        matchedCount: matchedIds?.length ?? current.matchedCount,
        truncatedIds: true,
        truncatedBySize: true,
      }),
      current,
      fits,
    );
  }

  if (fits()) {
    return current;
  }

  return {
    type: "resource_batch_analysis",
    resource: result.resource,
    question: result.question,
    scannedCount: result.scannedCount,
    filteredCount: result.filteredCount,
    analyzedCount: result.analyzedCount,
    batchCount: result.batchCount,
    matchedCount: result.matchedCount,
    truncatedBySize: true,
    warnings: [
      ...(result.warnings ?? []),
      "Batch analysis result was too large to send to the assistant. Retry with narrower filters or smaller caps.",
    ],
  };
}
